import { useMemo, useState } from 'react';
import type { Metro } from '../../types/metro';
import { RATIO_RANGE_BOUNDS, useFilterStore } from '../../stores/filterStore';
import { useFilteredMetros } from '../../hooks/useFilteredMetros';
import { EmptyState } from '../shared/EmptyState';
import { metroShortage } from '../../lib/utils';
import { MetroListItem } from './MetroListItem';
import { MapPanelHead } from './MapPanelHead';
import { MetroSearchCombobox } from './MetroSearchCombobox';
import { OccupationGroupCombobox } from '../shared/OccupationGroupCombobox';
import { ALL_OCCUPATIONAL_GROUPS, lensedRatioForMetro, slugifyGroupName } from '../../lib/occupation-utils';
import { RatioRangeSlider } from '../shared/RatioRangeSlider';
import { RatioMethodologyNote } from '../rankings/RatioMethodologyNote';
import { MapListHeader } from './MapListHeader';
import { DEFAULT_SORT_ASC, type SortKey } from './map-list-header-constants';

interface MapSidebarProps {
  readonly metros: ReadonlyArray<Metro>;
  readonly selectedMetroId: string | null;
  readonly onSelectMetro: (id: string) => void;
}

/**
 * Floating overlay panel for the map page. Layout per `_archive/map.html`
 * lines 126-257:
 *
 *   <aside.map-panel>          ← absolute top-4 left-4 bottom-4, w-sidebar
 *     <header.panel-head>      ← MapPanelHead (eyebrow + title + subtitle)
 *     <input.panel-search>     ← MetroSearchCombobox (mx-5 mt-[14px])
 *     <section.panel-filters>  ← OccupationGroupCombobox + StatusChips
 *     <header.panel-list-head> ← MapListHeader (count + sort dropdown)
 *     <ul.panel-list>          ← scrollable MetroListItem list
 *
 * No wrapper padding/gap on the panel shell — each section carries its
 * own padding so the borders between sections sit edge-to-edge.
 */
export function MapSidebar({ metros, selectedMetroId, onSelectMetro }: MapSidebarProps) {
  const search = useFilterStore((s) => s.search);
  const setSearch = useFilterStore((s) => s.setSearch);
  const ratioRange = useFilterStore((s) => s.ratioRange);
  const setRatioRange = useFilterStore((s) => s.setRatioRange);
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const setOccupationalGroup = useFilterStore((s) => s.setOccupationalGroup);
  const reset = useFilterStore((s) => s.reset);

  const filtered = useFilteredMetros(metros);
  const [sortKey, setSortKey] = useState<SortKey>('ratio');
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  /** Picking a new sort key from the dropdown resets direction to that
   *  key's sensible default (see DEFAULT_SORT_ASC above) rather than
   *  carrying over whatever direction the previous key was in. */
  const handleSortKeyChange = (key: SortKey) => {
    setSortKey(key);
    setSortAsc(DEFAULT_SORT_ASC[key]);
  };
  const toggleSortDirection = () => setSortAsc((asc) => !asc);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let cmp: number;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'credentials':
          cmp = a.totalCredentials - b.totalCredentials;
          break;
        case 'jobs':
          cmp = a.totalJobs - b.totalJobs;
          break;
        case 'shortage':
          // Reuses the same max(0, jobs - credentials) formula already
          // used for the Rankings "Annual credential shortage" CSV
          // column and the Map popup's per-group shortage cell --
          // see metroShortage() JSDoc in lib/utils.ts.
          cmp = metroShortage(a) - metroShortage(b);
          break;
        case 'ratio':
        default:
          // CR-039: sort on the figure the row DISPLAYS — the lensed
          // ratio under an OG filter (falls back to metro-level when
          // no lens is active / the metro lacks the group).
          cmp =
            lensedRatioForMetro(a, occupationalGroupId).ratio -
            lensedRatioForMetro(b, occupationalGroupId).ratio;
      }
      return sortAsc ? cmp : -cmp;
    });
    return list;
  }, [filtered, sortKey, sortAsc, occupationalGroupId]);

  return (
    <aside
      // Floating overlay panel per prototype lines 126-138. No internal
      // padding/gap on this shell — sections own their padding so the
      // border-bottom dividers sit flush.
      className="absolute bottom-4 left-4 top-4 z-[400] flex w-sidebar flex-col overflow-hidden rounded-lg border border-border bg-white shadow-panel"
      aria-label="Metro filters and list"
    >
      <MapPanelHead />

      {/* Search — custom brand-styled combobox. Native <datalist>
          had two problems: webkit rendered its own chevron stacking
          on ours, and the picker dropdown isn't styleable so its
          colors clashed with the rest of the platform. Replaced
          with a controlled <ul> dropdown whose items use brand
          tokens (bg-ice hover, text-navy, blue focus ring). */}
      <MetroSearchCombobox
        metros={metros}
        value={search}
        onChange={setSearch}
      />

      {/* Filters block — occupation chips above status chips. Prototype
          padding 12/20/14 + border-bottom (lines 174-178). */}
      <section className="flex-shrink-0 border-b border-border px-5 pb-[14px] pt-3">
        {/* PR B (combobox plan, 2026-06-01): swapped the plain
            OccupationGroupSelect for the typeahead
            OccupationGroupCombobox so the sidebar matches the rest
            of the platform (Detail/Rankings/Providers). The sidebar
            container is 288px wide with px-5 padding -> ~248px of
            inner room, which fits the input + magnifier + chevron
            comfortably; the combobox itself uses w-full so it adapts
            to the available width. */}
        <div className="mb-[14px]">
          <OccupationGroupCombobox
            value={occupationalGroupId ?? ''}
            onChange={(id) => setOccupationalGroup(id || null)}
            groups={ALL_OCCUPATIONAL_GROUPS.map((name) => ({
              id: slugifyGroupName(name),
              name,
              color: '#0074CC',
            }))}
            label="Occupational Group"
          />
        </div>
        {/* 2026-07-02 (Georgetown feedback pass): reviewers said the binary
            Shortage / Surplus chips forced a false choice — most map
            exploration happens INSIDE a shortage band (e.g. "show the
            0.4-0.8 sliver") rather than across the parity line.
            Replaced the two-chip toggle with a dual-thumb slider whose
            bounds match the legend's gradient domain (0-2.0). The 1.0
            parity mark is drawn as a gold tick on the track so the old
            shortage/surplus mental model stays legible. */}
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-text-4">
            Credentials-to-Jobs Ratio
          </p>
          {(ratioRange[0] !== RATIO_RANGE_BOUNDS[0] ||
            ratioRange[1] !== RATIO_RANGE_BOUNDS[1]) && (
            <button
              type="button"
              onClick={() => setRatioRange(RATIO_RANGE_BOUNDS)}
              className="text-[10px] font-semibold text-blue-link hover:underline"
            >
              Reset
            </button>
          )}
        </div>
        <div className="mt-1">
          <RatioRangeSlider
            min={RATIO_RANGE_BOUNDS[0]}
            max={RATIO_RANGE_BOUNDS[1]}
            step={0.05}
            value={ratioRange}
            onChange={setRatioRange}
            ariaLabel="Filter metros by credentials-to-jobs ratio"
            inflection={1.0}
          />
        </div>
      </section>

      {/* 2026-05-22 feedback: render the methodology disclosure as a
          full-width pill with the label centered, so it reads as a
          sidebar section header rather than a tight floating chip.
          Hover or focus reveals the full text in the tooltip. */}
      <div className="flex-shrink-0 px-5 pb-3 pt-2">
        <RatioMethodologyNote variant="compact" />
      </div>

      <MapListHeader
        count={sorted.length}
        sortKey={sortKey}
        sortAsc={sortAsc}
        onSortKeyChange={handleSortKeyChange}
        onToggleDirection={toggleSortDirection}
      />

      {/* Scrollable list — flex-1 fills remaining height, overscroll-
          contain so wheel events don't bleed to the page underneath. */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {sorted.length === 0 ? (
          <div className="px-5 py-6">
            <EmptyState
              title="No metros match"
              description="Try widening the ratio range or clearing the search."
              action={
                <button
                  type="button"
                  onClick={reset}
                  className="text-xs font-semibold text-blue-link hover:underline"
                >
                  Clear filters
                </button>
              }
            />
          </div>
        ) : (
          <ul>
            {sorted.map((metro) => (
              <li key={metro.id}>
                <MetroListItem
                  metro={metro}
                  isSelected={metro.id === selectedMetroId}
                  onSelect={onSelectMetro}
                  occupationalGroupId={occupationalGroupId}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
